import { Component, Input, Output, EventEmitter, inject, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import {
  ReceiptUploadsApiService,
  TransactionReceiptPrepRequest,
} from '../../../../../../_services/receipt-uploads-api-service/receipt-uploads-api.service';
import { UploadInput, UploadOutput, UploadFile, NgxUploaderModule, UploaderOptions } from 'ngx-uploader';
import { Subject, takeUntil } from 'rxjs';
import { NgClass } from '@angular/common';
import { AlertModule } from 'ngx-bootstrap/alert';
import { NgbProgressbar } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../../../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';

enum UploaderState {
  init = 0,
  selectFile = 1,
  uploading = 3,
}

/**
 * Custom upload component specifically for transaction receipts
 * Handles the 3-step upload flow:
 * 1. Prepare upload (get S3 params)
 * 2. Upload to S3
 * 3. Mark as complete on backend
 */
@Component({
  selector: 'app-receipt-upload',
  standalone: true,
  imports: [NgClass, NgxUploaderModule, AlertModule, NgbProgressbar],
  providers: [ReceiptUploadsApiService],
  templateUrl: './receipt-upload.component.html',
  styleUrl: '../../../../../../../static/styles/components/file-upload.scss',
})
export class ReceiptUploadComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;

  @Input() transactionId!: number;
  @Input() title: string = 'Drag your receipt here';
  @Input() text: string = 'or click the button to upload your receipt';
  @Input() textExtra: string = '';

  @Output() uploadSuccess = new EventEmitter<{ uploadId: string; file: UploadFile }>();
  @Output() batchUploadComplete = new EventEmitter<{
    successCount: number;
    failureCount: number;
    failures: Array<{ fileName: string; error: string }>;
  }>();
  @Output() uploadError = new EventEmitter<{ error: string; stage: 'prep' | 'upload' | 'complete' }>();

  private readonly receiptApi = inject(ReceiptUploadsApiService);
  private readonly destroy$ = new Subject<void>();

  // Uploader state
  state = UploaderState.selectFile;
  UploaderState = UploaderState;
  uploadInput = new EventEmitter<UploadInput>();
  dragOver = false;
  uploadProgress = 0;
  errorMessage = '';
  currentFile: UploadFile | null = null;
  currentUploadId: string | null = null;

  // Upload options - backend validates file size during prep call
  options: UploaderOptions = {
    concurrency: 3,
  };

  // Batch upload tracking
  private readonly currentBatch: Map<
    string,
    {
      file: UploadFile;
      uploadId?: string;
      status: 'pending' | 'prep' | 'uploading' | 'uploaded' | 'completed' | 'failed';
      error?: string;
      progress?: number; // Track individual file progress
    }
  > = new Map();
  private batchSuccessIds: string[] = [];
  public batchFailures: Array<{ fileName: string; error: string }> = [];
  private batchInProgress = false;
  private allFilesQueued = false; // Track when allAddedToQueue fires

  // Files waiting to be prepped - prep calls must run one at a time since
  // the backend validates/counts against the max-receipts limit during prep,
  // and concurrent prep calls can race past that check.
  private prepQueue: UploadFile[] = [];
  private preppingFile = false;

  /**
   * Start a new batch - clear all previous batch state
   */
  private startNewBatch(): void {
    this.batchInProgress = true;
    this.allFilesQueued = false; // Reset the flag
    this.currentBatch.clear();
    this.batchSuccessIds = [];
    this.batchFailures = [];
    this.errorMessage = '';
  }

  onUploadOutput(output: UploadOutput): void {
    switch (output.type) {
      case 'allAddedToQueue':
        this.allFilesQueued = true;
        // Now check if batch is complete (in case some files already failed during prep)
        this.checkBatchComplete();
        break;
      case 'addedToQueue':
        this.handleFileAddedToQueue(output.file);
        break;
      case 'uploading':
        this.handleFileUploading(output.file);
        break;
      case 'done':
        this.handleFileDone(output.file);
        break;
      case 'dragOver':
        this.dragOver = true;
        break;
      case 'dragOut':
      case 'drop':
        this.dragOver = false;
        break;
      case 'rejected':
        this.handleError('File rejected. Please check file type and size.', 'upload');
        break;
    }
  }

  private handleFileAddedToQueue(file: UploadFile | undefined): void {
    if (!file) return;

    // If this is the first file in a new batch, clear previous batch state
    if (!this.batchInProgress) {
      this.startNewBatch();
    }

    // Add to batch tracking
    const fileId = file.id;
    this.currentBatch.set(fileId, {
      file: file,
      status: 'pending',
    });
    this.currentFile = file;
    this.prepQueue.push(file);
    this.processPrepQueue();
  }

  /**
   * Process the prep queue one file at a time. Prep calls must not run
   * concurrently since the backend's max-receipts check happens during
   * prep - firing them in parallel lets multiple files race past the
   * limit before any of them are counted.
   */
  private processPrepQueue(): void {
    if (this.preppingFile) return;

    const file = this.prepQueue.shift();
    if (!file) return;

    this.preppingFile = true;
    this.startUpload(file);
  }

  private handleFileUploading(file: UploadFile | undefined): void {
    if (!file) return;

    const fileProgress = file.progress?.data?.percentage || 0;
    const fileId = file.id;
    const batchItem = this.currentBatch.get(fileId);
    if (batchItem) {
      batchItem.status = 'uploading';
      batchItem.progress = fileProgress;
    }

    // Calculate overall batch progress
    this.updateBatchProgress();
  }

  /**
   * Calculate overall progress for the entire batch
   * Takes into account all files: pending, uploading, uploaded, completed, and failed
   */
  private updateBatchProgress(): void {
    const allFiles = Array.from(this.currentBatch.values());
    if (allFiles.length === 0) {
      this.uploadProgress = 0;
      return;
    }

    let totalProgress = 0;
    allFiles.forEach((item) => {
      switch (item.status) {
        case 'pending':
          totalProgress += 0; // Not started
          break;
        case 'prep':
          totalProgress += 10; // Prep complete is 10%
          break;
        case 'uploading':
          totalProgress += 10 + (item.progress || 0) * 0.8; // 10% for prep + 80% for upload
          break;
        case 'uploaded':
          totalProgress += 90; // Upload done, waiting for completion
          break;
        case 'completed':
          totalProgress += 100; // Fully done
          break;
        case 'failed':
          totalProgress += 0; // Failed files count as 0%
          break;
      }
    });

    this.uploadProgress = Math.round(totalProgress / allFiles.length);
  }

  handleFileDone(file: UploadFile | undefined): void {
    if (!file) return;

    const fileId = file.id;
    const batchItem = this.currentBatch.get(fileId);
    if (batchItem?.uploadId) {
      batchItem.status = 'uploaded';
      batchItem.progress = 100;
      // Mark as ready for completion but don't call API yet
      this.batchSuccessIds.push(batchItem.uploadId);
      this.updateBatchProgress();
      this.checkBatchComplete();
    }
  }

  startUpload(file: UploadFile): void {
    this.state = UploaderState.uploading;
    this.errorMessage = '';
    this.uploadProgress = 0;

    // Step 1: Prepare upload
    const prepRequest: TransactionReceiptPrepRequest = {
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      transaction: this.transactionId,
    };

    this.receiptApi
      .prepareReceiptUpload(prepRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.currentUploadId = response.id;

          // Update batch tracking with uploadId
          const fileId = file.id;
          const batchItem = this.currentBatch.get(fileId);
          if (batchItem) {
            batchItem.uploadId = response.id;
            batchItem.status = 'prep';
          }
          this.updateBatchProgress();

          // Step 2: Upload to S3 with the params from backend
          let url = response.params.url;
          if (!url) {
            const errorMessage = 'Could not retrieve upload URL';
            this.handleError(errorMessage, 'prep');

            // Track failure in batch
            const fileId = file.id;
            const batchItem = this.currentBatch.get(fileId);
            if (batchItem) {
              batchItem.status = 'failed';
              batchItem.error = errorMessage;
            }
            this.batchFailures.push({ fileName: file.name, error: errorMessage });
            this.updateBatchProgress();
            this.checkBatchComplete();

            // Prep step settled (failed) - allow the next queued file to prep
            this.preppingFile = false;
            this.processPrepQueue();
            return;
          }

          // LOCALSTACK WORKAROUND (only for local development)
          // When running locally with LocalStack, the backend (in Docker) returns
          // host.docker.internal:4566, but the browser needs localhost:4566
          if (environment.name === 'default' && url.includes('host.docker.internal')) {
            url = url.replace('host.docker.internal', 'localhost');
          }

          // `response.params` contains more than the S3 form fields - it also
          // includes `meta`, `download_url`, and top-level duplicates of the
          // signed fields. Only `response.params.fields` is the exact set of
          // fields allowed by the signed POST policy. Sending anything else
          // (e.g. `meta`, `fields` itself, `download_url`, `url`) as a form
          // field causes real S3 (Dev/UAT/Prod) to reject the request with
          // InvalidAccessKeyId/SignatureDoesNotMatch, since it isn't part of
          // the signed POST policy. The backend always returns this same
          // nested shape (local/LocalStack included), so `fields` is used
          // for every environment - only the `url` host rewrite above is
          // environment-specific (LocalStack-only).
          const formFields: Record<string, string> = {
            ...(response.params.fields as Record<string, string>),
            filename: file.name,
          };

          const uploadEvent: UploadInput = {
            type: 'uploadFile',
            file: file,
            url: url,
            method: 'POST',
            data: formFields,
          };
          this.uploadInput.emit(uploadEvent);

          // Prep step settled (succeeded) - allow the next queued file to prep.
          // The S3 upload itself proceeds independently/concurrently as before.
          this.preppingFile = false;
          this.processPrepQueue();
        },
        error: (error: HttpErrorResponse | Error) => {
          let errorMessage = this.extractErrorMessage(error);

          if (errorMessage.toLowerCase().includes('invalid file')) {
            errorMessage = 'Invalid file type.';
          }

          // Track failure in batch
          const fileId = file.id;
          const batchItem = this.currentBatch.get(fileId);
          if (batchItem) {
            batchItem.status = 'failed';
            batchItem.error = errorMessage;
          }
          this.batchFailures.push({ fileName: file.name, error: errorMessage });
          this.errorMessage = errorMessage;
          this.updateBatchProgress();
          this.checkBatchComplete();

          // Prep step settled (failed) - allow the next queued file to prep
          this.preppingFile = false;
          this.processPrepQueue();
        },
      });
  }

  /**
   * Check if all files in the batch have completed (success or failure)
   * If so, make a single completion call for all successful uploads
   */
  private checkBatchComplete(): void {
    // Don't check completion until all files are queued
    if (!this.allFilesQueued) return;

    const allFiles = Array.from(this.currentBatch.values());
    const pendingFiles = allFiles.filter(
      (item) => item.status === 'pending' || item.status === 'prep' || item.status === 'uploading',
    );

    if (pendingFiles.length === 0 && allFiles.length > 0) {
      // All files have either succeeded or failed

      if (this.batchSuccessIds.length > 0) {
        // Complete all successful uploads in a single call
        this.completeBatchUploads();
      } else {
        // All failed - just reset
        this.state = UploaderState.selectFile;
        this.emitBatchResult();
      }
    }
  }

  /**
   * Emit single batch result for reloading transaction data
   */
  private completeBatchUploads(): void {
    // Snapshot the upload IDs to complete - `batchSuccessIds` gets mutated
    // (entries removed on failure) as we go, so iterate over a fixed copy.
    const uploadIds = [...this.batchSuccessIds];

    this.completeNextUpload(uploadIds, 0);
  }

  /**
   * Complete uploads one at a time - the next call only starts once the
   * previous one has settled (whether it succeeded or failed).
   */
  private completeNextUpload(uploadIds: string[], index: number): void {
    if (index >= uploadIds.length) {
      this.state = UploaderState.selectFile;
      this.emitBatchResult();
      return;
    }

    const uploadId = uploadIds[index];

    this.receiptApi
      .completeReceiptUpload(uploadId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Update batch item status
          const batchItem = Array.from(this.currentBatch.values()).find((item) => item.uploadId === uploadId);
          if (batchItem) {
            batchItem.status = 'completed';
          }
          this.updateBatchProgress();

          this.completeNextUpload(uploadIds, index + 1);
        },
        error: (error: HttpErrorResponse | Error) => {
          const baseMessage = this.extractErrorMessage(error);
          const errorMessage = 'Failed to complete upload: ' + baseMessage;

          // Move from success to failure
          this.batchSuccessIds = this.batchSuccessIds.filter((id) => id !== uploadId);
          const batchItem = Array.from(this.currentBatch.values()).find((item) => item.uploadId === uploadId);
          if (batchItem) {
            batchItem.status = 'failed';
            batchItem.error = errorMessage;
            this.batchFailures.push({ fileName: batchItem.file.name, error: errorMessage });
          }
          this.updateBatchProgress();

          this.completeNextUpload(uploadIds, index + 1);
        },
      });
  }

  /**
   * Emit the batch result with aggregated success/failure counts
   */
  private emitBatchResult(): void {
    const successCount = this.batchSuccessIds.length;
    const failureCount = this.batchFailures.length;

    this.batchUploadComplete.emit({
      successCount,
      failureCount,
      failures: this.batchFailures,
    });

    // Mark batch as complete
    this.batchInProgress = false;

    // Note: We keep batchFailures and errorMessage to display errors
    // They will be cleared when the next batch starts (in startNewBatch)
    // or when the user dismisses the error alert
  }

  /**
   * Group batch failures by error message so that identical errors
   * (e.g. "Maximum number of receipts reached" triggered by many files
   * at once) are only displayed once, while distinct errors are still
   * shown individually per file.
   */
  get failureGroups(): Array<{ error: string; fileNames: string[] }> {
    const groups = new Map<string, string[]>();
    for (const failure of this.batchFailures) {
      const fileNames = groups.get(failure.error) ?? [];
      fileNames.push(failure.fileName);
      groups.set(failure.error, fileNames);
    }
    return Array.from(groups.entries()).map(([error, fileNames]) => ({ error, fileNames }));
  }

  private handleError(message: string, stage: 'prep' | 'upload' | 'complete'): void {
    this.errorMessage = message;
    this.state = UploaderState.selectFile;
    this.uploadError.emit({ error: message, stage });
    // Don't call reset() here - it clears the error message immediately
  }

  /**
   * Extract error message from HTTP error response
   * Handles various error formats from the backend
   */
  private extractErrorMessage(error: HttpErrorResponse | Error): string {
    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      const errorBody = error.error;

      // Handle field-specific errors (e.g., { "transaction": ["error message"] })
      const fieldErrors = ['transaction', 'file_name', 'file_type', 'file_size'];
      for (const field of fieldErrors) {
        if (errorBody?.[field]) {
          // If it's an array, join the messages
          if (Array.isArray(errorBody[field])) {
            return errorBody[field].join(', ');
          }
          // If it's a string, return it
          return errorBody[field];
        }
      }

      // Handle generic message field
      if (errorBody?.message) {
        return errorBody.message;
      }

      // Handle detail field (common in DRF)
      if (errorBody?.detail) {
        return errorBody.detail;
      }

      // Fallback for HttpErrorResponse
      return error.message || 'Failed to prepare upload';
    }

    // Handle generic Error
    return error.message || 'An unexpected error occurred';
  }

  reset(): void {
    this.currentFile = null;
    this.currentUploadId = null;
    this.uploadProgress = 0;
    this.errorMessage = '';
    this.state = UploaderState.selectFile;
    this.currentBatch.clear();
    this.batchSuccessIds = [];
    this.batchFailures = [];
    this.batchInProgress = false;
    this.allFilesQueued = false;
    this.prepQueue = [];
    this.preppingFile = false;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
